"use client";
import React, { useMemo } from "react";
import Section from "./Section";
import SectionHeader from "./SectionHeader";
import Image from "next/image";
import FeedbackElement from "./FeedbackElement";
import Comment from "./Comment";
import { useParams } from "next/navigation";
import { useQuery } from "@tanstack/react-query";
import ehjzleeInstance from "@/lib/ehjzleeInstance";
import config from "@/lib/config";
import { format } from "date-fns";
import feedbackStar from "@/public/images/feedback-star.svg";
import { arSA, enUS } from "date-fns/locale";
import { useLocale, useTranslations } from "next-intl";

const getFeedback = async (id: string) => {
  try {
    const res = await ehjzleeInstance.get(
      `${config.props}/${id}/${config.reviews}`,
      {
        params: {
          size: 1000,
          page: 1,
        },
      }
    );
    return res.data;
  } catch (error) {
    console.log(error);
  }
};

const Feedback = ({
  hostname,
  cleanliness,
  comfort,
  information,
  overallRating,
  staff,
}: {
  hostname?: string;
  comfort: number;
  cleanliness: number;
  information: number;
  staff: number;
  overallRating: number;
}) => {
  const { unit } = useParams();
  const unitId = useMemo(() => unit, [unit]);
  const locale = useLocale();
  const t = useTranslations();
  const { data } = useQuery({
    queryKey: ["feedback", unitId],
    queryFn: () => getFeedback(unitId as string),
  });

  return (
    <Section>
      <SectionHeader>
        {data?.count ? t("feedback.header", { count: data?.count }) : ""}
        <span className="flex text-gray-dark gap-1 items-center uppercase">
          <Image src={feedbackStar} alt="star" width={24} height={24} />
          <span>{data?.count ? overallRating : t("newProp")}</span>
        </span>
      </SectionHeader>
      <div className="grid grid-cols-2 max-sm:grid-cols-1 border rounded-2xl p-5 gap-x-5 gap-y-2">
        <FeedbackElement name={t("feedback.cleanliness")} rate={cleanliness} />
        <FeedbackElement
          name={t("feedback.informationAccuracy")}
          rate={information}
        />
        <FeedbackElement name={t("feedback.staffInteraction")} rate={staff} />
        <FeedbackElement name={t("feedback.comfort")} rate={comfort} />
      </div>
      <div className="space-y-5">
        {data?.reviews?.map((review: any) => (
          <Comment
            key={review.guestReviewCreatedAt}
            name={`${review?.guest.firstName} ${review?.guest.lastName}`}
            rate={review?.reviewOverallRatingOfProperty}
            date={
              review?.guestReviewCreatedAt
                ? format(review?.guestReviewCreatedAt, "dd LLLL y", {
                    locale: locale === "ar" ? arSA : enUS,
                  })
                : "------"
            }
            hostname={hostname}
            reply={review?.ownerReply}
          >
            {review?.guestComment}
          </Comment>
        ))}
      </div>
    </Section>
  );
};

export default Feedback;
